
import { useState, useEffect } from "react"
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TableSortLabel } from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import CloseIcon from '@mui/icons-material/Close';
import { dateFormatter, capitalizeEachFirstLetterFormatter } from "../../../../utils/formatters";
import BondStatusChip from "../../../../components/BondStatusChip";
import { useNavigate } from "react-router-dom";

const headCells = [
    { id: 'isin', label: 'ISIN', numeric: false },
    { id: 'issuerName', label: 'Issuer', numeric: false },
    { id: 'country', label: 'Country', numeric: false },
    { id: 'maturity', label: 'Maturity', numeric: false },
    { id: 'currency', label: 'Currency', numeric: false },
    { id: 'creditRating', label: 'Rating', numeric: false },
    { id: 'rate', label: 'Coupon', numeric: true },
    { id: 'yield', label: 'YTM', numeric: true },
    { id: 'price', label: 'Price', numeric: true },
    { id: 'status', label: 'Status', numeric: false },
    { id: 'actions', label: '', numeric: 'right' }
];


const descendingComparator = (a, b, orderBy) => {
    const valueA = a[orderBy]
    const valueB = b[orderBy]
    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
}

const getComparator = (order, orderBy) => {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}


const DynamicAnalysisTableHead = ({ order, orderBy, onRequestSort }) => {
    return (
        <TableHead>
            <TableRow>
                {
                    headCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.numeric ? 'right' : 'left'}
                            sortDirection={orderBy === headCell.id ? order : false}
                        >
                            {headCell.id !== "actions" && <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={() => onRequestSort(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>}
                        </TableCell>
                    ))
                }
            </TableRow>
        </TableHead>
    )
}

const AnalysisTable = ({ instruments, handleSortSync, removeInstrument }) => {

    const navigate = useNavigate();

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('yield');

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRemoveInstrument = (event, instrumentId) => {
        event.stopPropagation()
        removeInstrument(instrumentId)
    }

    useEffect(() => {
        handleSortSync(instruments.sort(getComparator(order, orderBy)))
    }, [instruments, order, orderBy]
    );

    return (
        <TableContainer
            component={Paper}
        >
            <Table aria-label="simple table">
                <DynamicAnalysisTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                />
                <TableBody>
                    {instruments.map((row) => (
                        <TableRow
                            key={row.isin}
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                "&:hover:not(.Mui-disabled)": {
                                    cursor: "pointer"
                                }
                            }}
                            onClick={() => navigate('/bond/' + row.isin)}
                            hover={true}
                        >
                            <TableCell component="th" scope="row">
                                {row.isin}
                            </TableCell>
                            <TableCell >{capitalizeEachFirstLetterFormatter(row.issuerName)}</TableCell>
                            <TableCell >{row.country}</TableCell>
                            <TableCell >{dateFormatter(row.maturity, 'short')}</TableCell>
                            <TableCell >{row.currency}</TableCell>
                            <TableCell >{row.creditRating}</TableCell>
                            <TableCell align="right">{row.rate.toFixed(2)}%</TableCell>
                            <TableCell align="right">{row.yield.toFixed(2)}%</TableCell>
                            <TableCell align="right">{row.price.toFixed(2)}%</TableCell>
                            <TableCell><BondStatusChip status={row.status} maturity={row.maturity} chipSize={'small'} /></TableCell>
                            <TableCell ><IconButton size="small" color="complementary" onClick={(event) => handleRemoveInstrument(event, row.isin)}><CloseIcon fontSize="inherit" /></IconButton></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AnalysisTable;